import { ENV, trpc } from "@src/utils"
import { Pressable, View } from "react-native"
import { MenuActions, Text, Image, MCIcon } from "@src/components"
import { Link } from "expo-router"
import { ar } from "@src/i18n"

type CollectionCardProps = {
  collection: {
    id: number
    name: string
    status: "active" | "inactive"
    productsCount?: number | null
    images?: { key: string; thumbhash?: string | null }[]
  }
  onRefresh: () => void
  onEdit: () => void
}

export function CollectionCard({ collection, onRefresh, onEdit }: CollectionCardProps) {
  const toggleStatus = trpc.menu.toggleCollectionStatus.useMutation()
  const deleteCollection = trpc.menu.deleteCollection.useMutation()

  const isArchived = collection.status === "inactive"
  const collectionImage = collection.images?.at(-1)
  const isPending = toggleStatus.isPending || deleteCollection.isPending

  const handleToggleStatus = (newStatus: "active" | "inactive") => {
    toggleStatus.mutate({ id: collection.id, status: newStatus }, { onSuccess: onRefresh })
  }

  const handleDelete = async () => {
    try {
      await deleteCollection.mutateAsync({ id: collection.id })
      onRefresh()
      return undefined
    } catch (error) {
      return error instanceof Error ? error.message : "Failed to delete"
    }
  }

  return (
    <View className={`relative ${isArchived ? "opacity-50" : ""}`}>
      <Link href={`/dashboard/menu/collection/${collection.id}`} asChild>
        <Pressable className="bg-gray-100 rounded overflow-hidden active:opacity-70">
          {collectionImage ? (
            <Image
              source={`${ENV.R2_URL}/${collectionImage.key}`}
              style={{ width: 150, height: 150 }}
              contentFit="contain"
              loading="lazy"
              recyclingKey={collection.id.toString()}
              cachePolicy="memory"
              transition={250}
            />
          ) : (
            <View className="w-full h-full items-center justify-center bg-gray-200" style={{ width: 150, height: 150 }}>
              <MCIcon name="image-off" size={32} color="#ccc" />
            </View>
          )}
          <View className="absolute bottom-0 left-0 right-0 bg-black/50 p-2">
            <Text className="text-white text-sm font-medium" numberOfLines={1}>
              {collection.name}
            </Text>
            <Text className="text-white/80 text-xs">
              {collection.productsCount} {ar.products}
            </Text>
          </View>
          {isArchived && (
            <View className="absolute top-2 start-2 bg-yellow-500 px-2 py-0.5 rounded">
              <Text className="text-xs text-white font-semibold">{ar.inactive}</Text>
            </View>
          )}
        </Pressable>
      </Link>
      <MenuActions itemName={collection.name} status={collection.status} onToggleStatus={handleToggleStatus} onDelete={handleDelete} onEdit={onEdit} isPending={isPending} />
    </View>
  )
}
